import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import PropTypes from "prop-types";
import {createPortal} from "react-dom";
import {DocumentSearchIcon, PlusIcon} from "@heroicons/react/outline";
import {getProduct, getProducts} from "../../../api/catalog";
import placeholder from "../../../resources/images/placeholder.jpeg";
import {DOCUMENT_PORTAL_DROPDOWN_Z_INDEX_CLASS} from "../constants";
import {StatusBarIcon} from "../../../components/common/alert/status-bar-icon";
import {EditIcon} from "lucide-react";

const resolveProductPicture = (currentPicture, product) => {
    if (typeof currentPicture === "string" && currentPicture.trim()) {
        return currentPicture;
    }
    if (currentPicture && typeof currentPicture === "object") {
        return currentPicture.reference ?? currentPicture.url ?? null;
    }
    const pictures = Array.isArray(product?.pictures) ? product.pictures : [];
    const asset = pictures.find((item) => item?.reference || item?.url);
    return asset?.reference ?? asset?.url ?? null;
};

const resolveProductLabel = (value, product) => {
    return value ?? product?.name ?? "";
};

const ReadOnlyProductLookupCell = ({value, product, currentPicture, t}) => {
    const resolvedLabel = resolveProductLabel(value, product);
    const resolvedPicture = resolveProductPicture(currentPicture, product);
    return (
        <div className="flex items-center gap-2">
            {resolvedPicture && (
                <img
                    src={resolvedPicture}
                    alt={resolvedLabel || t("app.documents.product_name", {defaultValue: "Prodotto"})}
                    className="h-8 w-8 flex-none rounded-md border border-gray-200 object-cover object-center"
                    onError={(event) => {
                        event.currentTarget.src = placeholder;
                    }}
                />
            )}
            <span className="truncate text-sm text-gray-900 text-wrap">
                {resolvedLabel || "-"}
            </span>
        </div>
    );
};

const EditableProductLookupCell = ({
    value,
    onCommit,
    onSelectProduct,
    currentPicture,
    companyId,
    t,
    onCreateProduct,
    onEditProduct,
    product,
    onOpenAdvancedSearch,
}) => {

    const [inputValue, setInputValue] = useState(value ?? "");
    const [searchTerm, setSearchTerm] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const requestControllerRef = useRef(null);
    const initialValueRef = useRef(value ?? "");
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);

    useEffect(() => {
        const nextValue = value ?? "";
        setInputValue(nextValue);
        setSearchTerm(nextValue);
        initialValueRef.current = nextValue;
    }, [value]);

    useEffect(() => () => {
        if (requestControllerRef.current) {
            requestControllerRef.current.abort();
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setSuggestions([]);
        setHighlightedIndex(-1);
        setDropdownPosition(null);
    }, []);

    const commitValue = useCallback(() => {
        const normalized = inputValue ?? "";
        if (normalized === initialValueRef.current) {
            return;
        }
        initialValueRef.current = normalized;
        if (typeof onCommit === "function") {
            onCommit(normalized);
        }
    }, [inputValue, onCommit]);

    const fetchSuggestions = useCallback((term) => {
        if (!companyId) {
            setSuggestions([]);
            setIsFetching(false);
            return;
        }
        if (requestControllerRef.current) {
            requestControllerRef.current.abort();
        }
        const controller = new AbortController();
        requestControllerRef.current = controller;
        setIsFetching(true);
        getProducts({
            company_id: companyId,
            params: {limit: 10, offset: 0, query: term},
            signal: controller.signal,
        })
            .then((response) => {
                const list = Array.isArray(response?.products) ? response.products.slice(0, 10) : [];
                setSuggestions(list);
            })
            .catch(() => {
                if (controller.signal.aborted) {
                    return;
                }
                setSuggestions([]);
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsFetching(false);
                }
            });
    }, [companyId]);

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [suggestions]);

    const updateDropdownPosition = useCallback(() => {
        if (typeof window === "undefined" || !containerRef.current) {
            return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY,
            width: rect.width,
        });
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const term = (searchTerm ?? "").trim();
        if (term.length < 2) {
            setSuggestions([]);
            setIsFetching(false);
            if (requestControllerRef.current) {
                requestControllerRef.current.abort();
            }
            updateDropdownPosition();
            return undefined;
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(term);
            updateDropdownPosition();
        }, 250);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [fetchSuggestions, isOpen, searchTerm, updateDropdownPosition]);

    useLayoutEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        updateDropdownPosition();
        const handleRelayout = () => {
            updateDropdownPosition();
        };
        if (typeof window !== "undefined") {
            window.addEventListener("resize", handleRelayout);
            window.addEventListener("scroll", handleRelayout, true);
        }
        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", handleRelayout);
                window.removeEventListener("scroll", handleRelayout, true);
            }
        };
    }, [isOpen, updateDropdownPosition]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && containerRef.current.contains(event.target)) {
                return;
            }
            if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
                return;
            }
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                commitValue();
                closeDropdown();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [closeDropdown, commitValue]);

    const resolveProductDetails = useCallback(async (product) => {
        if (!companyId || !product?.id) {
            return product;
        }
        if (Array.isArray(product?.product_dimensions) && product.product_dimensions.length > 0) {
            return product;
        }
        try {
            const detailed = await getProduct({company_id: companyId, product_id: product.id});
            if (detailed) {
                return {...product, ...detailed};
            }
        } catch (error) {
            if (error?.name === "AbortError") {
                return product;
            }
        }
        return product;
    }, [companyId]);

    const selectProduct = useCallback(async (selectedProduct) => {
        const resolvedProduct = await resolveProductDetails(selectedProduct);
        closeDropdown();
        setInputValue(resolvedProduct?.name ?? "");
        setSearchTerm(resolvedProduct?.name ?? "");
        setSuggestions([]);
        setHighlightedIndex(-1);
        initialValueRef.current = resolvedProduct?.name ?? "";
        if (typeof onSelectProduct === "function") {
            onSelectProduct(resolvedProduct);
        }
        if (inputRef.current) {
            inputRef.current.blur();
        }
    }, [closeDropdown, onSelectProduct, resolveProductDetails]);

    const handleEditProduct = useCallback((event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        closeDropdown();
        if (typeof onEditProduct === "function") {
            onEditProduct(product);
        }
    }, [closeDropdown, onEditProduct, product]);

    const handleKeyDown = useCallback((event) => {
        if (isOpen && suggestions.length > 0) {
            if (event.key === "ArrowDown") {
                event.preventDefault();
                setHighlightedIndex((prev) => {
                    const next = prev + 1;
                    return next >= suggestions.length ? 0 : next;
                });
                return;
            }
            if (event.key === "ArrowUp") {
                event.preventDefault();
                setHighlightedIndex((prev) => {
                    const next = prev - 1;
                    return next < 0 ? suggestions.length - 1 : next;
                });
                return;
            }
            if (event.key === "Enter") {
                if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                    event.preventDefault();
                    selectProduct(suggestions[highlightedIndex]);
                    return;
                }
            }
            if (event.key === "Escape") {
                event.preventDefault();
                closeDropdown();
                return;
            }
        }
        if (event.key === "Enter") {
            commitValue();
            closeDropdown();
        }
    }, [closeDropdown, commitValue, highlightedIndex, isOpen, selectProduct, suggestions]);

    const hasProduct = Boolean(product?.id);

    const suggestionsList = useMemo(() => suggestions, [suggestions]);

    return (
        <>
            <div ref={containerRef} className="flex w-full items-center gap-1">
                {hasProduct && (
                    <img
                        src={currentPicture ?? placeholder}
                        alt={inputValue || t("app.documents.product_name", {defaultValue: "Prodotto"})}
                        className="h-8 w-8 flex-none rounded-md object-cover object-center border border-gray-200 -mr-2"
                        onError={(event) => {
                            event.currentTarget.src = placeholder;
                        }}
                    />
                )}
                <div className="relative w-full">
                    <input
                        ref={inputRef}
                        value={inputValue}
                        onChange={(event) => {
                            const nextValue = event.target.value;
                            setInputValue(nextValue);
                            setSearchTerm(nextValue);
                            setIsOpen(true);
                            requestAnimationFrame(updateDropdownPosition);
                        }}
                        onFocus={() => {
                            setIsOpen(true);
                            setSearchTerm((product?.product_reference  ?? (inputValue ?? "")));
                            requestAnimationFrame(updateDropdownPosition);
                        }}
                        onBlur={() => {
                            requestAnimationFrame(() => {
                                if (!containerRef.current) {
                                    return;
                                }
                                const active = document.activeElement;
                                if (!active || !containerRef.current.contains(active)) {
                                    commitValue();
                                    closeDropdown();
                                }
                            });
                        }}
                        onKeyDown={handleKeyDown}
                        type="text"
                        className="w-full bg-transparent px-2 py-1 text-sm text-gray-900 border-none focus:outline-none focus:ring-0"
                    />
                    {hasProduct && typeof onEditProduct === "function" && (
                        <div className="-mt-2 px-2 flex flex-wrap items-center gap-2">

                            {product?.status && (
                                <div className="text-xxs flex-wrap text-gray-500">
                                    {t("app.products.status", {defaultValue: "Stato"})}: <StatusBarIcon status={product.status} isText />
                                </div>
                            )}


                        </div>
                    )}
                </div>
            </div>

            {isOpen && dropdownPosition && typeof document !== "undefined" && createPortal(
                <div
                    ref={dropdownRef}
                    className={`${DOCUMENT_PORTAL_DROPDOWN_Z_INDEX_CLASS} mb-1 flex max-h-80 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg`}
                    style={{
                        position: "absolute",
                        left: dropdownPosition.left,
                        top: dropdownPosition.top - 4,
                        width: typeof dropdownPosition.width === "number"
                            ? Math.max(dropdownPosition.width, 360)
                            : dropdownPosition.width,
                        transform: "translateY(-100%)",
                    }}
                >
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {isFetching && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                {t("app.common.loading", {defaultValue: "Caricamento..."})}
                            </div>
                        )}
                        {!isFetching && suggestionsList.length === 0 && (searchTerm?.trim().length ?? 0) >= 2 && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                {t("app.catalog.no_products_found", {defaultValue: "Nessun prodotto trovato"})}
                            </div>
                        )}
                        {!isFetching && suggestionsList.length > 0 && (
                            <ul className="divide-y divide-gray-100">
                                {suggestionsList.map((suggestion, index) => {
                                    const picture = suggestion?.picture?.reference
                                        ?? (Array.isArray(suggestion?.pictures)
                                            ? suggestion.pictures.find((asset) => asset?.reference)?.reference
                                            : null);
                                    const extraDetail = suggestion?.manufacture_reference ?? null;
                                    const isHighlighted = index === highlightedIndex;
                                    return (
                                        <li key={suggestion.id || index}>
                                            <div
                                                className={isHighlighted
                                                    ? "block cursor-pointer bg-indigo-50/[0.5]"
                                                    : "block cursor-pointer hover:bg-indigo-50/[0.5]"}
                                                onMouseEnter={() => setHighlightedIndex(index)}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    selectProduct(suggestion);
                                                }}
                                                role="button"
                                                tabIndex={-1}
                                            >
                                                <div className="flex items-center p-1">
                                                    <div className="min-w-0 flex-1 flex items-center">
                                                        <div className="ml-2">
                                                            <img
                                                                className="object-contain h-12 w-12 rounded-md border border-gray-200"
                                                                src={picture ?? placeholder}
                                                                alt={suggestion?.name ?? ""}
                                                                onError={(event) => {
                                                                    event.currentTarget.src = placeholder;
                                                                }}
                                                            />
                                                        </div>

                                                        <div className="min-w-0 flex-1 px-4">
                                                            <div>
                                                                <p className="text-sm font-medium text-blue-1000 ">
                                                                    {suggestion?.product_reference ?? ""}
                                                                </p>
                                                                <p className="text-xs flex-wrap text-gray-800 -mb-1">
                                                                    {suggestion?.name ?? ""}
                                                                </p>
                                                                {extraDetail && (
                                                                    <b className="text-xs font-medium text-gray-500">
                                                                        {extraDetail}
                                                                    </b>
                                                                )}

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    {(typeof onCreateProduct === "function" || typeof onOpenAdvancedSearch === "function") && (
                        <div className="border-t border-gray-100 bg-gray-50">
                            <div className="divide-y divide-gray-100">
                                {typeof onCreateProduct === "function" && (
                                    <div className="flex items-center px-3 py-2">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                closeDropdown();
                                                onCreateProduct();
                                            }}
                                        >
                                            <PlusIcon className="h-4 w-4" aria-hidden="true" />
                                            <span>{t("app.documents.add_product_action", {defaultValue: "Aggiungi prodotto"})}</span>
                                        </button>
                                    </div>
                                )}
                                {typeof onOpenAdvancedSearch === "function" && (
                                    <div className="flex items-center px-3 py-2">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                closeDropdown();
                                                onOpenAdvancedSearch();
                                            }}
                                        >
                                            <DocumentSearchIcon className="h-4 w-4" aria-hidden="true" />
                                            <span>{t("app.documents.advanced_product_search_action", {defaultValue: "Ricerca avanzata"})}</span>
                                        </button>
                                    </div>
                                )}

                                {hasProduct && typeof onEditProduct === "function" && (
                                    <div className="flex items-center px-3 py-2">

                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                closeDropdown();
                                                handleEditProduct();
                                            }}
                                        >
                                            <EditIcon className="h-4 w-4" aria-hidden="true" />
                                            <span> {t("app.documents.edit_product_action", {defaultValue: "Modifica prodotto"})}</span>

                                        </button>

                                    </div>
                                )}

                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
};

const DocumentBodyProductLookupCell = (props) => (
    props.isEditable
        ? <EditableProductLookupCell {...props} />
        : <ReadOnlyProductLookupCell {...props} />
);

DocumentBodyProductLookupCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCommit: PropTypes.func,
    onSelectProduct: PropTypes.func,
    currentPicture: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    t: PropTypes.func.isRequired,
    onCreateProduct: PropTypes.func,
    onEditProduct: PropTypes.func,
    product: PropTypes.object,
    onOpenAdvancedSearch: PropTypes.func,
    isEditable: PropTypes.bool,
};

DocumentBodyProductLookupCell.defaultProps = {
    value: "",
    onCommit: undefined,
    onSelectProduct: undefined,
    currentPicture: null,
    companyId: null,
    onCreateProduct: undefined,
    onEditProduct: undefined,
    product: null,
    onOpenAdvancedSearch: undefined,
    isEditable: true,
};

export default DocumentBodyProductLookupCell;
